import React from "react";
import { useLocation } from "react-router-dom";
import { BodyMeasurementResult } from "../../../utilities/types/unified-services";
import SizeGuideQuestionnaire from "./SizeGuideQuestionnaire";
import AnatomyVisualization from "./AnatomyVisualization";

interface BodyMeasurementResultsProps {
  result: BodyMeasurementResult;
  uploadedImage?: string;
}

const BodyMeasurementResults: React.FC<BodyMeasurementResultsProps> = ({ result, uploadedImage }) => {
  const location = useLocation();
  const isGuestMode = location.pathname.includes("/guest/");
  const [showSizeGuide, setShowSizeGuide] = React.useState<boolean>(true);

  const handleBrandOwnerCheck = (isBrandOwner: boolean) => {
    setShowSizeGuide(isBrandOwner);
  };

  const formatMeasurement = (value: number | string) => {
    if (typeof value === "number") {
      return value.toFixed(2);
    }
    return value;
  };

  const getAllMeasurements = () => {
    const measurements = [];

    const measurementOrder = [
      { key: "Shoulder", label: "Shoulder" },
      { key: "Chest", label: "Chest" },
      { key: "UnderChest", label: "Under Chest" },
      { key: "UpperBody", label: "Upper Body" },
      { key: "ArmLength", label: "Arm Length" },
      { key: "Waist", label: "Waist" },
      { key: "Hips", label: "Hips" },
      { key: "LowerBody", label: "Lower Body" },
      { key: "PantWaist", label: "Pant Waist" },
      { key: "Knee", label: "Knee" },
      { key: "Neck", label: "Neck" },
      { key: "Wrist", label: "Wrist" },
    ];

    measurementOrder.forEach(({ key, label }) => {
      if (result[key as keyof BodyMeasurementResult] !== undefined && result[key as keyof BodyMeasurementResult] !== null) {
        measurements.push({
          key,
          label,
          value: result[key as keyof BodyMeasurementResult] as number,
          unit: "cm",
        });
      }
    });

    return measurements;
  };

  const measurements = getAllMeasurements();

  return (
    <div className="w-full max-w-6xl space-y-8">
      {/* <div className="!m-0 p-6 text-center border bg-primary/5 rounded-2xl border-primary/10">
        <h2 className="text-2xl text-secondary">Body Measurement Results</h2>
        <p className="m-0 text-lg text-gray-600 ">Your complete body measurements analysis</p>
      </div> */}

      <div className="p-4 space-y-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
        <AnatomyVisualization gender={result.Gender} bodyType={result.BodyType} measurements={measurements} result={result} />

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {measurements.map((measurement) => (
            <div key={measurement.key} className="p-4 transition-all duration-200 border border-gray-200 bg-gradient-to-br from-gray-50 to-white rounded-xl hover:shadow-md hover:border-primary/30">
              <div className="text-center">
                <div className="mb-2 text-sm font-medium text-gray-700">{measurement.label}</div>
                <div className="mb-1 text-2xl font-bold text-secondary">
                  {formatMeasurement(measurement.value)}

                  <span className="text-sm font-medium tracking-wide text-gray-500 uppercase px-0.5">{measurement.unit}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isGuestMode && showSizeGuide && (
        <div className="flex flex-col items-center w-full p-4 border bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl border-primary/20">
          <div className="mb-6 text-center">
            <h3 className="mb-2 text-2xl text-secondary">Find Your Perfect Size</h3>
            <p className="text-sm text-gray-600">Get personalized size recommendations based on your measurements</p>
          </div>
          <div className="flex justify-center w-full p-4 bg-white border border-gray-200 rounded-xl">
            <SizeGuideQuestionnaire onBrandOwnerCheck={handleBrandOwnerCheck} />
          </div>
        </div>  
      )}

      {isGuestMode && (
        <div className="flex flex-col items-center w-full p-4 border bg-gradient-to-br from-blue/5 to-primary/5 rounded-2xl border-blue/20">
          <div className="mb-4 text-center">
            <h3 className="mb-2 text-xl text-secondary">Want Size Recommendations?</h3>
            <p className="text-sm text-gray-600">Register for free to get personalized size recommendations based on your measurements</p>
          </div>
          <a href={`/${location.pathname.split("/")[1]}/register`} className="px-6 py-3 font-medium !text-white transition-all duration-300 rounded-lg bg-primary hover:bg-primary/90 hover:shadow-md">
            Register for Free
          </a>
        </div>
      )}
    </div>
  );
};

export default BodyMeasurementResults;
